import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { MachineConfigApi } from '@/api';
import { Edit, RefreshCw } from 'lucide-react';

const FAMILY_LABELS: Record<string, string> = {
  nova: 'Nova', nano: 'Nano', orbiton: 'Orbiton', roll_format: 'Roll Format',
};

const EMPTY_FORM = {
  min_price_inr: '', max_price_inr: '',
  min_price_usd: '', max_price_usd: '',
  min_price_eur: '', max_price_eur: '',
};

export default function PriceConfig() {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [family, setFamily] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editCfg, setEditCfg] = useState<any>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [converting, setConverting] = useState(false);

  const configsQuery = useQuery({
    queryKey: ['machineConfigsPricing', family],
    queryFn: () => MachineConfigApi.list({ ...(family && { family }) }),
  });
  const configs = configsQuery.data?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: (data: any) => MachineConfigApi.updatePrice(editCfg!.id, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Price band updated' });
      setModalOpen(false);
      setEditCfg(null);
      setErrors({});
      queryClient.invalidateQueries({ queryKey: ['machineConfigsPricing'] });
      queryClient.invalidateQueries({ queryKey: ['machineConfigsAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['machineConfigs'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update price band', body: err.message });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    (['inr', 'usd', 'eur'] as const).forEach((curr) => {
      const min = formData[`min_price_${curr}` as keyof typeof formData];
      const max = formData[`max_price_${curr}` as keyof typeof formData];
      if ((min && !max) || (!min && max)) {
        newErrors[`price_${curr}`] = `Both min and max required for ${curr.toUpperCase()}`;
      }
      if (min && max && Number(min) > Number(max)) {
        newErrors[`price_${curr}`] = 'Min cannot be greater than max';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenEdit = (cfg: any) => {
    setFormData({
      min_price_inr: cfg.min_price_inr ?? '', max_price_inr: cfg.max_price_inr ?? '',
      min_price_usd: cfg.min_price_usd ?? '', max_price_usd: cfg.max_price_usd ?? '',
      min_price_eur: cfg.min_price_eur ?? '', max_price_eur: cfg.max_price_eur ?? '',
    });
    setEditCfg(cfg);
    setErrors({});
    setModalOpen(true);
  };

  // Convenience autofill only — never authoritative. Uses live ECB reference
  // rates from Frankfurter (free, no API key). Admin can still edit the
  // filled-in USD/EUR values before saving; this just gives a starting point
  // instead of blank fields, since USD/EUR bands may deliberately differ from
  // a strict conversion (rounding, per-market pricing, currency-risk margin).
  const handleAutoFillFromInr = async () => {
    const inrMin = Number(formData.min_price_inr);
    const inrMax = Number(formData.max_price_inr);
    if (!inrMin || !inrMax) {
      push({ tone: 'error', title: 'Set the INR price range first' });
      return;
    }
    setConverting(true);
    try {
      const res = await fetch('https://api.frankfurter.dev/v1/latest?from=INR&to=USD,EUR');
      if (!res.ok) throw new Error(`Rate lookup failed (HTTP ${res.status})`);
      const data = await res.json();
      const usdRate = data?.rates?.USD;
      const eurRate = data?.rates?.EUR;
      if (!usdRate || !eurRate) throw new Error('Rate response did not include USD/EUR');
      setFormData((f) => ({
        ...f,
        min_price_usd: String(Math.round(inrMin * usdRate)),
        max_price_usd: String(Math.round(inrMax * usdRate)),
        min_price_eur: String(Math.round(inrMin * eurRate)),
        max_price_eur: String(Math.round(inrMax * eurRate)),
      }));
      push({ tone: 'success', title: `Filled from live rates (${data.date})`, body: 'Review and adjust before saving.' });
    } catch (err: any) {
      push({ tone: 'error', title: 'Could not fetch live rates', body: err.message ?? 'Enter USD/EUR manually.' });
    } finally {
      setConverting(false);
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;
    updateMutation.mutate({
      min_price_inr: formData.min_price_inr ? Number(formData.min_price_inr) : null,
      max_price_inr: formData.max_price_inr ? Number(formData.max_price_inr) : null,
      min_price_usd: formData.min_price_usd ? Number(formData.min_price_usd) : null,
      max_price_usd: formData.max_price_usd ? Number(formData.max_price_usd) : null,
      min_price_eur: formData.min_price_eur ? Number(formData.min_price_eur) : null,
      max_price_eur: formData.max_price_eur ? Number(formData.max_price_eur) : null,
    });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Price Config"
        subtitle="Set the min/max price band for every machine configuration. A PI cannot be created with a config that has no price band set. Admin only."
      />

      <div className="card card-pad flex flex-wrap gap-4 items-end">
        <div>
          <label className="label">Family</label>
          <select className="input mt-1" value={family} onChange={(e) => setFamily(e.target.value)}>
            <option value="">All</option>
            <option value="nova">Nova</option>
            <option value="nano">Nano</option>
            <option value="orbiton">Orbiton</option>
            <option value="roll_format">Roll Format</option>
          </select>
        </div>
      </div>

      {configsQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : configs.length === 0 ? (
        <EmptyState
          title="No machine configurations found"
          body="Add machine configurations first in Machine Configs, then set their price bands here."
        />
      ) : (
        <div className="card flex flex-col md:max-h-[calc(100vh-260px)]">
          <div className="overflow-auto flex-1">
            <table className="table-clean">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Family</th>
                  <th>Name</th>
                  <th>Price Range (INR)</th>
                  <th>Price Range (USD)</th>
                  <th>Price Range (EUR)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c: any) => (
                  <tr key={c.id}>
                    <td className="text-sm text-muted">{FAMILY_LABELS[c.family]}</td>
                    <td className="font-medium">{c.name}</td>
                    <td className="text-sm">
                      {c.min_price_inr && c.max_price_inr
                        ? `₹${Number(c.min_price_inr).toLocaleString('en-IN')} - ₹${Number(c.max_price_inr).toLocaleString('en-IN')}`
                        : <span className="text-rdx">Not set</span>}
                    </td>
                    <td className="text-sm">
                      {c.min_price_usd && c.max_price_usd
                        ? `$${Number(c.min_price_usd).toLocaleString('en-US')} - $${Number(c.max_price_usd).toLocaleString('en-US')}`
                        : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-sm">
                      {c.min_price_eur && c.max_price_eur
                        ? `€${Number(c.min_price_eur).toLocaleString('en-US')} - €${Number(c.max_price_eur).toLocaleString('en-US')}`
                        : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-ghost" onClick={() => handleOpenEdit(c)}>
                        <Edit size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditCfg(null); setErrors({}); }}
        title={editCfg ? `Price Band — ${editCfg.name}` : 'Price Band'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setModalOpen(false); setEditCfg(null); setErrors({}); }}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {(['inr', 'usd', 'eur'] as const).map((curr) => (
            <div key={curr} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
              <h4 className="text-sm font-medium mb-2">Price Range ({curr.toUpperCase()})</h4>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="label">Min</label>
                  <input
                    type="number"
                    className={`input mt-1 ${errors[`price_${curr}`] ? 'input-error' : ''}`}
                    value={formData[`min_price_${curr}` as keyof typeof formData]}
                    onChange={(e) => setFormData({ ...formData, [`min_price_${curr}`]: e.target.value })}
                  />
                </div>
                <div className="flex-1">
                  <label className="label">Max</label>
                  <input
                    type="number"
                    className={`input mt-1 ${errors[`price_${curr}`] ? 'input-error' : ''}`}
                    value={formData[`max_price_${curr}` as keyof typeof formData]}
                    onChange={(e) => setFormData({ ...formData, [`max_price_${curr}`]: e.target.value })}
                  />
                </div>
              </div>
              {errors[`price_${curr}`] && <div className="text-xs text-rdx mt-1">{errors[`price_${curr}`]}</div>}
              {curr === 'inr' && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary gap-1.5 mt-3"
                  onClick={handleAutoFillFromInr}
                  disabled={converting}
                >
                  <RefreshCw size={13} className={converting ? 'animate-spin' : ''} />
                  {converting ? 'Fetching live rates…' : 'Auto-fill USD & EUR from INR'}
                </button>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
